import { Component } from '@angular/core';
import { CreateRoomService } from './create-room.service';
import { combineLatest, Subscription } from 'rxjs';
import { FooterService } from '../../../../reusable-components/footer/footer.service';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import {
  getSortedUsageSpaceCategories,
  getUsageSpaceCategoryWithEmoji,
  UsageSpaceCategory,
} from '../../../../lead.model';
import {
  KeyValue,
  KeyValueTableComponent,
} from '../../../../reusable-components/key-value-table/key-value-table.component';
import { KeyValueTableService } from '../../../../reusable-components/key-value-table/key-value-table.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { MatStepperService } from '../../lead-task-services/mat-stepper.service';
import { LeadTaskService } from '../../lead-task.service';

@Component({
  selector: 'app-create-room',
  imports: [KeyValueTableComponent],
  templateUrl: './create-room.component.html',
  styleUrl: './create-room.component.scss',
  providers: [CreateRoomService],
})
export class CreateRoomComponent {
  matStepperSubscription: Subscription;
  keyValues: KeyValue[];

  constructor(
    private readonly matStepperService: MatStepperService,
    private readonly createRoomService: CreateRoomService,
    private readonly footerService: FooterService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
    private readonly leadTaskService: LeadTaskService,
  ) {
    this.keyValues = this.initializeKeyValueTable();
    this.matStepperSubscription = this.getMatStepperSubscription();
  }

  getMatStepperSubscription(): Subscription {
    return combineLatest([
      this.matStepperService.currentStep$,
      this.createRoomService.getNgComponentOutletInputObservable(),
      this.createRoomService.errorListService.errors$,
    ]).subscribe(([currentIndex, ngComponentOutletInput, errors]) => {
      if (
        currentIndex === 1 &&
        ngComponentOutletInput.component === CreateRoomComponent
      ) {
        this.footerService.setRightButton({
          label: 'components.create-room.save',
          click: () => this.createRoomService.startSelfCreatedRoomExamination(),
          disabled: errors.length > 1,
        });
        this.footerService.setLeftButton({
          label: 'components.create-room.discard',
          click: () => this.createRoomService.discardSelfCreatedRoom(),
        });
      }
    });
  }

  initializeKeyValueTable(): KeyValue[] {
    const keyValueTableService = KeyValueTableService.create(
      this.translateService,
      this.dialogService,
      this.snackbarService,
    );

    const keyValueTable: KeyValue[] = [
      keyValueTableService.createUpdateKeyValuePair({
        key: 'components.usage-space.information-table.name',
        updateValueFn: (value: string) => this.createRoomService.setName(value),
        getValueFn: () => this.createRoomService.getName(),
      }),
    ];

    // Zeige Zimmeranzahl nur, wenn Raumkategorie HOTEL_GAESTEZIMMER ist
    const usageSpaceCategory = this.createRoomService.getCategory();

    if (usageSpaceCategory === 'HOTEL_GAESTEZIMMER') {
      keyValueTable.push(
        keyValueTableService.createUpdateKeyValuePair({
          key: 'components.usage-space.information-table.numberRooms',
          updateValueFn: (value: string) =>
            this.createRoomService.setNumberRooms(Number.parseInt(value)),
          getValueFn: () => this.createRoomService.getNumberRooms().toString(),
          acceptInputFn: (value: string) =>
            this.leadTaskService.validateUsageSpaceNumberRooms(value),
          errorMessage: 'components.create-room.number-rooms-error',
          type: 'number',
        }),
      );
    }

    keyValueTable.push(this.getRoomCategoryKeyValuePair());

    // Zeige Raumgröße nur, wenn Raumkategorie nicht HOTEL_GAESTEZIMMER ist
    if (usageSpaceCategory !== 'HOTEL_GAESTEZIMMER') {
      keyValueTable.push(
        keyValueTableService.createUpdateKeyValuePair({
          key: 'components.usage-space.information-table.size',
          updateValueFn: (value: string) => this.createRoomService.setSize(value),
          getValueFn: () => this.createRoomService.getSize().toString(),
          acceptInputFn: (value: string) =>
            this.leadTaskService.validateUsageSpaceSize(value),
          errorMessage: 'components.room-examination.size-error',
          type: 'number',
        }),
      );
    }

    return keyValueTable;
  }

  getRoomCategoryKeyValuePair(): KeyValue {
    const keyValue: KeyValue = {
      key: 'components.usage-space.information-table.category',
      value: getUsageSpaceCategoryWithEmoji(
        this.createRoomService.getCategory(),
        this.translateService,
      ),
      tryUpdate: () => {
        const onSelect = (option: string) => {
          this.createRoomService.setCategory(option as UsageSpaceCategory);
          keyValue.value = getUsageSpaceCategoryWithEmoji(
            option as UsageSpaceCategory,
            this.translateService,
          );
          this.snackbarService.show('components.input-dialog.snackbar');
          // Nach Kategorie-Auswahl KeyValueTable neu initialisieren
          this.keyValues = this.initializeKeyValueTable();
        };
        this.dialogService.openSelectDialog(
          getSortedUsageSpaceCategories(this.translateService)
            .filter(
              (category) =>
                category !== 'HOTEL_GAESTEZIMMER' ||
                this.leadTaskService.getLeadCategory() ===
                'GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG',
            )
            .map((category) => {
              return {
                option: category,
                label: getUsageSpaceCategoryWithEmoji(category, this.translateService),
              };
            }),
          'components.usage-space.information-table.category',
          'components.create-room.room-category-question',
          onSelect,
          undefined,
          this.createRoomService.getCategory(),
        );
      },
      translateValue: false,
    };
    return keyValue;
  }
}
